import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

// Roles that see the "On-Duty" splash toast on successful login.
// Clients are excluded — they aren't going "on-duty".
const ON_DUTY_ROLES = new Set([
  'main_hub', 'department_admin', 'super_admin', 'admin',
  'field_investigator', 'investigator', 'training_director',
  'lead_investigator', 'supervisor',
]);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const tryApplicantLogin = async () => {
    const res = await fetch(`${BACKEND_URL}/api/applicant-auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.detail || 'Invalid credentials');
    localStorage.setItem('nexus_applicant_token', data.token);
    localStorage.setItem('nexus_applicant_id', data.applicant?.id || '');
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // NEXUS-XXXX usernames are typically applicants, so we try applicant-auth first.
    // BUT once an applicant is promoted to an investigator, their staff user record
    // may still carry a NEXUS-XXXX username (forced first-sign-in flow). If applicant
    // auth rejects, fall back to staff /api/auth/login before reporting Invalid credentials.
    const looksLikeApplicant = /^NEXUS-/i.test(username.trim());
    const routeAfterStaffLogin = (data) => {
      if (data?.user?.require_username_change || data?.user?.require_password_change) {
        navigate('/complete-setup');
      } else {
        // On-Duty splash — only for operational roles (not clients).
        const role = data?.user?.role;
        if (role && ON_DUTY_ROLES.has(role)) {
          const displayName = data?.user?.first_name || data?.user?.username || 'Investigator';
          toast.success(`${displayName} — you are now On-Duty`, {
            description: `Signed in as ${role.replace(/_/g, ' ')}. Shift started ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`,
            duration: 5000,
            icon: <ShieldCheck className="text-emerald-400" size={18} />,
          });
        }
        navigate('/dashboard');
      }
    };
    try {
      if (looksLikeApplicant) {
        try {
          await tryApplicantLogin();
          navigate('/applicant-portal');
          return;
        } catch (applicantErr) {
          // Promoted-investigator fallback — same NEXUS-XXXX is now a staff user.
          const data = await login(username, password);
          routeAfterStaffLogin(data);
          return;
        }
      }
      const data = await login(username, password);
      routeAfterStaffLogin(data);
    } catch (err) {
      // Fallback: if staff login failed, try applicant auth before reporting error.
      try {
        await tryApplicantLogin();
        navigate('/applicant-portal');
        return;
      } catch {
        // Normalize FastAPI validation payloads (arrays / dicts) to a
        // single readable string. Any other truthy err.message wins.
        let msg = err?.message;
        if (Array.isArray(msg)) {
          msg = msg.map((m) => m?.msg || m).join('; ');
        } else if (msg && typeof msg === 'object') {
          msg = msg.msg || JSON.stringify(msg);
        }
        if (!msg || /^\[object /i.test(String(msg))) {
          msg = 'We couldn\u2019t sign you in. Double-check your username and password, or contact your admin if the problem continues.';
        } else if (/invalid credentials/i.test(msg)) {
          msg = 'The username or password you entered is incorrect.';
        }
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto overflow-x-hidden bg-[#030812] flex items-center justify-center p-4 py-8 relative" data-testid="login-shell">
      {/* Layered premium background: Dallas skyline + eagle composite photo */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Unified Dallas skyline + bald eagle hero photo */}
        <div
          className="absolute inset-0 bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(/nexus-hero-dallas-eagle.jpg)',
            backgroundPosition: 'center center',
            filter: 'brightness(0.75) saturate(1.05)',
          }}
        />
        {/* Deep navy vignette wash */}
        <div className="absolute inset-0" style={{
          background:
            'radial-gradient(60% 55% at 50% 55%, rgba(3,8,18,0.35), rgba(3,8,18,0.75) 75%),' +
            'linear-gradient(180deg, rgba(3,8,18,0.35) 0%, rgba(3,8,18,0.75) 100%)',
        }} />
        {/* Faint grid overlay for tactical feel */}
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage:
            'linear-gradient(rgba(96,165,250,0.4) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(96,165,250,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />
      </div>

      <div className="w-full max-w-sm space-y-5 relative z-10 my-auto">
        {/* Brand block */}
        <div className="text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-amber-400/10 blur-2xl rounded-full" aria-hidden />
            <img src="/nexustrac-logo-nobg.png" alt="NexusTrac" className="relative w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 mx-auto mb-2 object-contain" data-testid="login-logo" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold !text-white tracking-[0.08em]">NEXUSTRAC</h1>
          <p className="text-[10px] font-mono !text-amber-300/80 uppercase tracking-[0.32em] mt-1.5">Investigation Intelligence Platform</p>
        </div>

        {/* Glass card */}
        <div className="relative rounded-xl p-[1px] bg-gradient-to-b from-amber-300/40 via-white/10 to-blue-500/30 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]">
          <div className="bg-[#0A1628]/85 backdrop-blur-xl border border-white/5 rounded-xl p-6">
            <h2 className="text-base font-heading font-semibold !text-white mb-1">Sign In</h2>
            <p className="text-xs !text-slate-400 mb-5">Enter credentials to access the platform</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-sm !text-red-300 text-sm">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs !text-slate-300 uppercase tracking-wider font-mono">Username</Label>
              <Input type="text" value={username} onChange={e => setUsername(e.target.value)}
                className="bg-white/5 border-white/20 !text-white placeholder:text-slate-400 focus:border-amber-400 rounded-sm"
                placeholder="NEXUS-XXXX" required data-testid="username-input" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs !text-slate-300 uppercase tracking-wider font-mono">Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="bg-white/5 border-white/20 !text-white placeholder:text-slate-400 pr-10 focus:border-amber-400 rounded-sm"
                  placeholder="Enter password" required data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-heading font-semibold rounded-md shadow-[0_6px_18px_-6px_rgba(59,130,246,0.6)]"
              disabled={loading} data-testid="login-button">
              {loading ? 'Authenticating...' : 'Go On-Duty'}
            </Button>
          </form>
          </div>
        </div>

        {/* Public Client Intake Landing (Iter121A):
            Prospective clients tap this from the login screen and are taken
            to a public /client-intake page that explains services and hosts
            a detailed intake form. Submissions land in the internal Client
            Intake module for admin review — no portal login required. */}
        <div className="pt-1">
          <Link to="/client-intake">
            <Button
              variant="outline"
              className="w-full border-blue-400/40 !text-blue-200 bg-blue-500/10 hover:bg-blue-500/20 hover:border-blue-400/60 font-heading font-semibold rounded-md"
              data-testid="new-client-intake-btn"
              title="Request investigation services — no account required"
            >
              <img src="/nexustrac-logo-nobg.png" alt="" className="w-6 h-6 object-contain mr-2" />
              NEW CLIENT INTAKE
            </Button>
          </Link>
          <p className="text-[10px] text-slate-500 text-center mt-1">
            Prospective clients — request services without an account
          </p>
        </div>

        {/* Auxiliary links row */}
        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider pt-1">
          <Link to="/forgot-password" className="!text-slate-400 hover:!text-white" data-testid="forgot-password-link">Forgot password?</Link>
          <Link to="/apply" className="!text-amber-300 hover:!text-amber-200" data-testid="apply-for-nexus-link">Apply for Nexus →</Link>
        </div>

        {/* Iter124 — Public Submit-a-Tip entry (no auth) */}
        <div className="pt-1">
          <Link to="/tip" data-testid="submit-a-tip-link">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-md border-amber-500/50 !text-amber-300 hover:!text-amber-200 hover:!bg-amber-500/10 font-mono uppercase tracking-[0.18em] text-[11px]"
              data-testid="submit-a-tip-btn"
            >
              🛡 Submit A Tip · Anonymous
            </Button>
          </Link>
          <p className="text-[10px] text-slate-500 text-center mt-1">
            Report intel anonymously — routed straight to admin review
          </p>
        </div>

        {/* Main Hub Link */}
        <div className="text-center border-t border-white/5 pt-3 mt-1">
          <Link to="/main-hub/login"
            className="inline-flex items-center gap-2 text-[10px] !text-slate-500 hover:!text-slate-300 font-mono uppercase tracking-[0.2em]">
            <img src="/nexustrac-logo-nobg.png" alt="" className="w-5 h-5 object-contain" /> Main Hub Access
          </Link>
        </div>

        {/* Legal footer */}
        <div className="text-center pt-2">
          <p className="text-[9px] font-mono !text-slate-600 tracking-[0.2em] uppercase">
            Privacy · Terms · © Nexus Investigation Agency
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
